import React, { useEffect, useState } from 'react';

export default function CurrentPayPeriod() {
  const [groups, setGroups] = useState([]); // [{ user:{id,name,email}, hours, earnings, tasks:[...] }]
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // task or null
  const [users, setUsers] = useState([]); // dropdown options for assign
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);

  // repeating rules state
  const [rules, setRules] = useState([]);
  const emptyRule = { id: 0, name: '', description: '', hours: 1, days: 0, users: [] };
  const [ruleEditing, setRuleEditing] = useState(null);
  const [rulesVisible, setRulesVisible] = useState(false);
  const [selectedRuleIds, setSelectedRuleIds] = useState([]);

  const fetchTasks = async () => {
    try {
      const params = new URLSearchParams();
      if (start) params.append('start', start.toISOString());
      if (end) params.append('end', end.toISOString());
      const res = await fetch(`api/work/getcurrentperiodgrouped${params.toString() ? `?${ params }` : ''}`);
      const data = await res.json();
      setGroups(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch('api/work/getusers');
      const data = await res.json();
      setUsers(data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchRules = async () => {
    try {
      const res = await fetch('api/repeatingtasks/rules');
      if (res.ok) {
        const data = await res.json();
        setRules(data || []);
      }
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    const d = new Date();
    setStart(new Date(d.getFullYear(), d.getMonth(), 1));
    setEnd(new Date(d.getFullYear(), d.getMonth() + 1, 0));
    fetchRules();
  }, []);

  useEffect(() => {
    if (!start || !end) return;
    setLoading(true);
    fetchTasks();
    fetchUsers();
  }, [start, end]);

  const toCurrency = (n) => Number(n||0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  const toHours = (n) => Number(n||0).toLocaleString('en-US', { maximumFractionDigits: 2 });
  const toDow = (d) => new Date(d).toLocaleDateString(undefined, { weekday: 'short' });

  // Overall totals
  const totalEarnings = groups.reduce((s, g) => s + Number(g.earnings || 0), 0);

  const empty = { id: 0, date: new Date().toISOString(), userId: '', description: '', timeWorked: 0, timeHours: 0, timeMinutes: 0, wage: 0 };

  const splitTimeWorked = (tw) => {
    const fractional = Number(tw || 0);
    const h = Math.floor(fractional);
    const m = Math.round((fractional - h) * 60);
    return { h, m };
  };

  const startEdit = (t) => {
    if (t) {
      const { h, m } = splitTimeWorked(t.timeWorked);
      setEditing({ ...t, timeHours: h, timeMinutes: m });
    } else {
      setEditing({ ...empty });
    }
  };

  const startEditForUser = (userId) => setEditing({ ...empty, userId });
  const cancelEdit = () => setEditing(null);

  const ensureFractionalTimeWorked = (draft) => {
    const hours = Number(draft.timeHours || 0);
    const minutes = Number(draft.timeMinutes || 0);
    const fractional = hours + (minutes / 60);
    return { ...draft, timeWorked: fractional };
  };

  const onUserChange = (e) => {
    const userId = e.target.value;
    const u = users.find(x => String(x.id) === String(userId));
    setEditing(prev => ({
      ...prev,
      userId,
      // only override wage for new items (id = 0 or falsy)
      wage: (prev?.id && prev.id > 0) ? prev.wage : Number(u?.wage ?? 0)
    }));
  };

  const onHoursChange = (e) => {
    const value = e.target.value;
    setEditing(prev => ensureFractionalTimeWorked({ ...prev, timeHours: value }));
  };

  const onMinutesChange = (e) => {
    let value = e.target.value;
    // Clamp minutes 0-59
    const n = Math.max(0, Math.min(59, Number(value || 0)));
    setEditing(prev => ensureFractionalTimeWorked({ ...prev, timeMinutes: n }));
  };

  // Re-add the missing save function (add/update a work task)
  const save = async () => {
    try {
      const body = ensureFractionalTimeWorked({ ...editing });
      if (!body.userId) return; // require user
      if (typeof body.date !== 'string') {
        body.date = new Date(body.date).toISOString();
      }
      if (!body.description) return;
      if (body.id && body.id > 0) {
        const res = await fetch(`api/work/updatetask/${body.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        if (!res.ok) throw new Error('update failed');
      } else {
        const res = await fetch('api/work/addtask', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        if (!res.ok) throw new Error('add failed');
      }
      setEditing(null);
      await fetchTasks();
    } catch (e) {
      console.error(e);
    }
  };

  // Repeating rules helpers
  const DAY_FLAGS = {
    Sunday: 1,
    Monday: 2,
    Tuesday: 4,
    Wednesday: 8,
    Thursday: 16,
    Friday: 32,
    Saturday: 64,
  };
  const toggleDay = (day) => {
    setRuleEditing(prev => ({ ...prev, days: (prev.days ^ DAY_FLAGS[day]) }));
  };
  const isDayChecked = (day, daysValue) => (daysValue & DAY_FLAGS[day]) !== 0;
  const formatDays = (daysValue) => {
    const labels = Object.keys(DAY_FLAGS).filter(day => isDayChecked(day, daysValue));
    return labels.length ? labels.join(', ') : 'None';
  };

  const startRuleEdit = (rule) => {
    if (rule) {
      const mappedUsers = (rule.users || []).map(u => u.userId);
      setRuleEditing({ id: rule.id, name: rule.name, description: rule.description, hours: rule.hours, days: rule.days, users: mappedUsers });
    } else {
      setRuleEditing({ ...emptyRule });
    }
  };
  const cancelRuleEdit = () => setRuleEditing(null);

  const saveRule = async () => {
    try {
      const body = {
        id: ruleEditing.id || 0,
        name: ruleEditing.name,
        description: ruleEditing.description,
        hours: Number(ruleEditing.hours || 0),
        days: Number(ruleEditing.days || 0),
        users: (ruleEditing.users || []).map(uid => ({ userId: uid }))
      };
      const res = await fetch('api/repeatingtasks/rules', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('save rule failed');
      setRuleEditing(null);
      await fetchRules();
    } catch (e) { console.error(e); }
  };

  const deleteRule = async (id) => {
    try {
      const res = await fetch(`api/repeatingtasks/rules/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('delete rule failed');
      await fetchRules();
    } catch (e) { console.error(e); }
  };

  const applyRulesToMonth = async () => {
    if (!start) return;
    const y = start.getFullYear();
    const m = start.getMonth() + 1; // 1-based
    const query = new URLSearchParams();
    if (selectedRuleIds.length > 0) {
      query.append('ruleIds', selectedRuleIds.join(','));
    }
    try {
      const res = await fetch(`api/repeatingtasks/apply?year=${y}&month=${m}${query.toString() ? `&${query}` : ''}`, { method: 'POST' });
      if (!res.ok) throw new Error('apply failed');
      await fetchTasks();
    } catch (e) { console.error(e); }
  };

  const remove = async (id) => {
    try {
      const res = await fetch(`api/work/deletetask/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('delete failed');
      await fetchTasks();
    } catch (e) {
      console.error(e);
    }
  };

  const toggleRuleSelected = (id) => {
    setSelectedRuleIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const shiftMonth = (delta) => {
    if (!start) return;
    const newStart = new Date(start.getFullYear(), start.getMonth() + delta, 1);
    const newEnd = new Date(newStart.getFullYear(), newStart.getMonth() + 1, 0);
    setStart(newStart);
    setEnd(newEnd);
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <h2>Pay Period</h2>
      <p className="d-flex align-items-center gap-2"> 
        <button className="btn btn-outline-secondary btn-sm" onClick={() => shiftMonth(-1)}>&lt;</button>
        Period: {start.toLocaleDateString()} - {end.toLocaleDateString()}
        <button className="btn btn-outline-secondary btn-sm" onClick={() => shiftMonth(1)}>&gt;</button>        
      </p>
      <p>
        Total earnings: <b>{toCurrency(totalEarnings)}</b>
      </p>
      <div style={{ marginBottom: 16 }}>
        {!editing && <button className="btn btn-primary" onClick={() => startEdit(null)} style={{marginRight: 16}}>Add item</button>}
        <button className="btn btn-outline-secondary btn-sm" onClick={() => setRulesVisible(v => !v)}>{rulesVisible ? 'Hide' : 'Manage'} repeating rules</button>
      </div>

      {rulesVisible && (
        <div className="card p-3 mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Repeating rules</h5>
            {!ruleEditing && (
              <button className="btn btn-sm btn-primary" onClick={() => startRuleEdit(null)}>Add rule</button>
            )}
          </div>

          {ruleEditing && (
            <div className="border rounded p-3 mb-3">
              <div className="row g-2">
                <div className="col-md-3">
                  <label className="form-label">Name</label>
                  <input type="text" className="form-control" value={ruleEditing.name} onChange={e => setRuleEditing(r => ({ ...r, name: e.target.value }))} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Description</label>
                  <input type="text" className="form-control" value={ruleEditing.description} onChange={e => setRuleEditing(r => ({ ...r, description: e.target.value }))} />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Hours</label>
                  <input type="number" step="0.25" className="form-control" value={ruleEditing.hours} onChange={e => setRuleEditing(r => ({ ...r, hours: e.target.value }))} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Users</label>
                  <select multiple className="form-select" value={ruleEditing.users} onChange={e => {
                    const opts = Array.from(e.target.selectedOptions).map(o => o.value);
                    setRuleEditing(r => ({ ...r, users: opts }));
                  }}>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name || u.id}</option>)}
                  </select>
                </div>
                <div className="col-md-12">
                  <label className="form-label">Days of week</label>
                  <div className="d-flex flex-wrap gap-3">
                    {Object.keys(DAY_FLAGS).map(day => (
                      <label key={day} className="form-check-label">
                        <input type="checkbox" className="form-check-input me-1" checked={isDayChecked(day, ruleEditing.days)} onChange={() => toggleDay(day)} />
                        {day}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="col-md-12 mt-2">
                  <button className="btn btn-success me-2" onClick={saveRule}>Save rule</button>
                  <button className="btn btn-secondary" onClick={cancelRuleEdit}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          <div>
            {rules.length === 0 && <div className="text-muted">No rules yet.</div>}
            {rules.map(r => (
              <div key={r.id} className="d-flex justify-content-between align-items-center border rounded p-2 mb-2">
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" checked={selectedRuleIds.includes(r.id)} onChange={() => toggleRuleSelected(r.id)} id={`rule_${r.id}`} />
                </div>
                <div className="flex-grow-1 ms-2">
                  <div><strong>{r.name}</strong> &mdash; {r.description} &middot; Hours: {r.hours}</div>
                  <div className="text-muted small">Users: {(r.users||[]).map(u => users.find(x => x.id === u.userId)?.name || u.userId).join(', ')}</div>
                  <div className="text-muted small">Days: {formatDays(r.days)}</div>
                </div>
                <div className="d-flex gap-2">
                  <button className="btn btn-sm btn-outline-primary" onClick={() => startRuleEdit(r)}>Edit</button>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => deleteRule(r.id)}>Delete</button>
                </div>
              </div>
            ))}
            <button className="btn btn-outline-primary btn-sm" onClick={applyRulesToMonth}>Apply selected rules to this period</button>
          </div>
        </div>
      )}


      {editing && (
        <div className="card p-3 mb-3">
          <div className="row g-2">
            <div className="col-md-2">
              <label className="form-label">Date</label>
              <input type="date" className="form-control" value={editing.date?.substring(0,10)} onChange={e => setEditing({ ...editing, date: new Date(e.target.value).toISOString() })} />
            </div>
            <div className="col-md-2">
              <label className="form-label">User</label>
              <select className="form-select" value={editing.userId} onChange={onUserChange}>
                <option value="">Select user...</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name || u.id}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Description</label>
              <input type="text" className="form-control" value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })} />
            </div>
            <div className="col-md-1">
              <label className="form-label">Hours</label>
              <input type="number" min="0" step="1" className="form-control" value={editing.timeHours ?? 0} onChange={onHoursChange} />
            </div>
            <div className="col-md-1">
              <label className="form-label">Minutes</label>
              <input type="number" min="0" max="59" step="1" className="form-control" value={editing.timeMinutes ?? 0} onChange={onMinutesChange} />
            </div>
            <div className="col-md-1">
              <label className="form-label">Wage</label>
              <input type="number" step="0.01" className="form-control" value={editing.wage} onChange={e => setEditing({ ...editing, wage: e.target.value })} />
            </div>
            <div className="col-md-12 mt-2">
              <button className="btn btn-success me-2" onClick={save}>Save</button>
              <button className="btn btn-secondary" onClick={cancelEdit}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {groups.map(g => (
        <div key={g.user.id} style={{ marginBottom: 24 }}>
          <div className="d-flex align-items-center justify-content-between">
            <h4>{g.user.name || g.user.id} - {toHours(g.hours)} hours, {toCurrency(g.earnings)}</h4>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Day</th>
                <th>Date</th>
                <th>Description</th>
                <th>Hours</th>
                <th>Wage</th>
                <th>Earnings</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {g.tasks.map(t => (
                <tr key={t.id}>
                  <td>{toDow(t.date)}</td>
                  <td>{new Date(t.date).toLocaleDateString()}</td>
                  <td>{t.ruleId ? t.description : <strong>{t.description}</strong>}</td>
                  <td>{toHours(t.timeWorked)}</td>
                  <td>{toCurrency(t.wage)}</td>
                  <td>{toCurrency(Number(t.timeWorked||0)*Number(t.wage||0))}</td>
                  <td>
                    <button className="btn btn-sm btn-outline-primary me-2" onClick={() => startEdit({ ...t, userId: g.user.id })}>Edit</button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => remove(t.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="3"><strong>Totals</strong></td>
                <td><strong>{toHours(g.hours)}</strong></td>
                <td></td>
                <td><strong>{toCurrency(g.earnings)}</strong></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      ))}
    </div>
  );
}
